/**
 * Email Template Modal
 * View, create, and edit email templates
 */

import { useEffect, useState } from 'react';
import { useForm } from '@mantine/form';
import { IconTemplate, IconEye, IconCode } from '@tabler/icons-react';
import { Stack, Group, Button, Text, TextInput, Switch, Tabs, Textarea, SegmentedControl } from '../../../../../components/ui';
import { Modal, Paper, Code } from '../../../../../components/ui';
import { HtmlEditor } from '../../../../../components/ui/inputs';
import type { EmailTemplateModalProps } from '../types';

// UTF-8 safe base64 encoding/decoding
const encodeBase64 = (str: string): string => {
  const bytes = new TextEncoder().encode(str);
  const binString = Array.from(bytes, (byte) => String.fromCodePoint(byte)).join('');
  return btoa(binString);
};

const decodeBase64 = (base64: string): string => {
  try {
    const binString = atob(base64);
    const bytes = Uint8Array.from(binString, (char) => char.codePointAt(0) || 0);
    return new TextDecoder().decode(bytes);
  } catch {
    try {
      return atob(base64);
    } catch {
      return base64;
    }
  }
};

export function EmailTemplateModal({
  opened,
  onClose,
  template,
  mode,
  onSave,
  loading,
  onSwitchToEdit,
}: EmailTemplateModalProps & { onSwitchToEdit?: () => void }) {
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  // Toggle between visual editor and raw HTML input
  const [editorMode, setEditorMode] = useState<'visual' | 'code'>('visual');

  const form = useForm({
    initialValues: {
      templateName: '',
      subject: '',
      body: '',
      description: '',
      isActive: true,
    },
    validate: {
      templateName: (value) => {
        if (!value || value.trim() === '') return 'Template name is required';
        return null;
      },
      body: (value) => {
        if (!value || value.trim() === '') return 'Template body is required';
        return null;
      },
    },
  });

  useEffect(() => {
    if (template) {
      // Decode body if it's base64 encoded (handle UTF-8)
      const decodedBody = template.body ? decodeBase64(template.body) : '';

      form.setValues({
        templateName: template.templateName || '',
        subject: template.subject || '',
        body: decodedBody,
        description: (template as any).description || '',
        isActive: template.isActive ?? true,
      });
    } else if (isCreateMode) {
      form.reset();
    }
  }, [template, isCreateMode]);

  const handleSubmit = async (values: typeof form.values) => {
    if (onSave) {
      try {
        // Encode body to base64 for API (handle UTF-8 characters)
        const encodedBody = encodeBase64(values.body);

        // Backend expects AppEmailTemplate entity format, not view model format
        // Map: templateName -> Name, body -> Template, description -> Description
        const submitData = {
          // Include TemplateID for updates (backend validates id matches)
          ...(template?.templateID && { TemplateID: template.templateID }),
          Name: values.templateName,
          Template: encodedBody,
          Description: values.description || values.subject, // Backend uses Description for Subject
        };

        console.log('Submitting template data (entity format):', submitData);
        await onSave(submitData as any);
        console.log('Template saved successfully');
        form.reset();
        onClose();
      } catch (error) {
        console.error('Error saving template:', error);
        // Error is handled by the parent, don't close modal
      }
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        isCreateMode
          ? 'Create Email Template'
          : isEditMode
          ? 'Edit Email Template'
          : 'View Email Template'
      }
      
      size="xl"
    >
      {isViewMode && template ? (
        <Stack gap="md">
          <div>
            <Text fw={500} size="sm" mb={4}>
              Template Name
            </Text>
            <Text size="sm">{template.templateName}</Text>
          </div>

          <div>
            <Text fw={500} size="sm" mb={4}>
              Subject
            </Text>
            <Text size="sm">{template.subject || '(No Subject)'}</Text>
          </div>

          {/* Category field doesn't exist in AppEmailTemplateModelView */}

          <div>
            <Text fw={500} size="sm" mb={4}>
              Status
            </Text>
            <Text size="sm">{template.isActive ? 'Active' : 'Inactive'}</Text>
          </div>

          <Tabs defaultValue="preview">
            <Tabs.List>
              <Tabs.Tab value="preview" leftSection={<IconEye size={16} />}>
                Preview
              </Tabs.Tab>
              <Tabs.Tab value="source" leftSection={<IconCode size={16} />}>
                HTML Source
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="preview" pt="md">
              <Paper p="md" withBorder style={{ minHeight: '200px' }}>
                <div dangerouslySetInnerHTML={{
                  __html: template.body ? decodeBase64(template.body) : ''
                }} />
              </Paper>
            </Tabs.Panel>

            <Tabs.Panel value="source" pt="md">
              <Code block language="html">
                {template.body ? decodeBase64(template.body) : ''}
              </Code>
            </Tabs.Panel>
          </Tabs>

          <Group justify="space-between">
            <Button variant="subtle" onClick={handleClose}>
              Close
            </Button>
            {onSwitchToEdit && (
              <Button
                variant="light"
                leftSection={<IconTemplate size={16} />}
                onClick={onSwitchToEdit}
              >
                Edit Template
              </Button>
            )}
          </Group>
        </Stack>
      ) : (
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput
              label="Template Name"
              placeholder="e.g., Welcome Email"
              required
              {...form.getInputProps('templateName')}
            />

            <TextInput
              label="Subject"
              placeholder="Email subject line"
              {...form.getInputProps('subject')}
            />

            <TextInput
              label="Description"
              placeholder="Brief description of this template"
              {...form.getInputProps('description')}
            />

            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={500}>
                  Template Body <Text span c="red">*</Text>
                </Text>
                <SegmentedControl
                  size="xs"
                  value={editorMode}
                  onChange={(value) => setEditorMode(value as 'visual' | 'code')}
                  data={[
                    { label: 'Visual Editor', value: 'visual' },
                    { label: 'HTML Code', value: 'code' },
                  ]}
                />
              </Group>

              {editorMode === 'visual' ? (
                <Stack gap={4}>
                  <HtmlEditor
                    value={form.values.body}
                    onChange={(val) => form.setFieldValue('body', val)}
                    placeholder="Enter your HTML template..."
                  />
                  {form.errors.body && (
                    <Text size="xs" c="red">{form.errors.body}</Text>
                  )}
                </Stack>
              ) : (
                <Textarea
                  placeholder="Paste your HTML code here..."
                  value={form.values.body}
                  onChange={(e) => form.setFieldValue('body', e.currentTarget.value)}
                  minRows={15}
                  maxRows={25}
                  autosize
                  styles={{
                    input: {
                      fontFamily: 'monospace',
                      fontSize: '13px',
                    },
                  }}
                  error={form.errors.body}
                />
              )}
            </Stack>

            <Switch
              label="Active"
              description="Only active templates can be used"
              {...form.getInputProps('isActive', { type: 'checkbox' })}
            />

            <Group justify="flex-end" gap="xs">
              <Button variant="subtle" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" loading={loading} variant="light">
                {isCreateMode ? 'Create Template' : 'Save Changes'}
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
